// IE 2026 11 04 / ODOO-350449 (замечание Игоря: продолжение ячейки при наведении — только при обрезке и не поверх содержимого)
// «Продолжение» ячейки данных грида по наведению (utils/clipOverlay.js, AttrGrid.headerTooltip_show) — два правила:
// 1. показывается, только если текст ячейки действительно обрезан. Раньше решали scrollWidth ячейки и шириной Range всего содержимого:
//    у ячеек с абсолютными слоями (капсулы / кружки теплокарты, кнопка формы ячейки) и спрятанным текстом (.hmg-bcell — color: transparent)
//    оба замера больше ширины ячейки при целиком видимом содержимом — подложка всплывала «ни с чего». Теперь мерим прямоугольники:
//    каждая видимая строка текста, которую продолжение умеет показать (не в служебных, не в абсолютных и не в спрятанных элементах,
//    цвет не прозрачный), и каждая видимая «пилюля» потока (фон / рамка) — против коробки обрезки (ближайшие предки с overflow ≠ visible,
//    у текста с многоточием — по внутреннему полю), допуск 1 px;
// 2. если продолжение всё же показано, оно не закрывает слои самой ячейки (капсула, SVG, картинка, абсолютные элементы): в подложке
//    вырезаются «окна» по их прямоугольникам (clip-path evenodd) — под окном видна настоящая ячейка, пиксель в пиксель, без клонирования.
// Выключатель fe.ux.cellPeekTruncatedOnly (по умолчанию вкл; выкл — прежние замеры и подложка без окон).

export const CELL_PEEK_FLAG = 'fe.ux.cellPeekTruncatedOnly'
// служебные элементы — не текст (тот же список, что у clipOverlay: их продолжение не переносит)
export const CLIP_SKIP = 'button, input, select, textarea, i, svg, img, canvas, .pi, .cell-dropdown-toggle, .wj-elem-collapse, .wj-btn, .break-arrow, .chip-open-ico, [class*="wj-glyph"]'

export function cellPeek_on(setting) {
    try { return typeof setting !== 'function' || setting(CELL_PEEK_FLAG, true) !== false } catch (e) { return true }
}

// ---- чистая часть (юнит-тест tests/unit/cellPeek.test.mjs) ----

// item = { r: {left, top, right, bottom}, clip: {left, top, right, bottom} }: обрезан, если по горизонтали выходит за коробку
// больше чем на tol px, а по вертикали — если центр строки / элемента вне коробки (спрятанная строка переноса; выход на доли
// строки при высокой line-height — не обрезка)
export function cellPeek_sticksOut(items, tol = 1) {
    for (const it of items || []) {
        const r = it && it.r, c = it && it.clip
        if (!r || !c) continue
        if (!(r.right - r.left > 0.01) && !(r.bottom - r.top > 0.01)) continue   // пустой прямоугольник (пробел, нулевой элемент)
        if (r.right > c.right + tol || r.left < c.left - tol) return true
        const cy = (r.top + r.bottom) / 2
        if (cy > c.bottom || cy < c.top) return true
    }
    return false
}

export function rect_intersect(a, b) {
    if (!a) return b ? { left: b.left, top: b.top, right: b.right, bottom: b.bottom } : null
    if (!b) return { left: a.left, top: a.top, right: a.right, bottom: a.bottom }
    return { left: Math.max(a.left, b.left), top: Math.max(a.top, b.top), right: Math.min(a.right, b.right), bottom: Math.min(a.bottom, b.bottom) }
}

const n2 = v => Math.round(v * 100) / 100
// clip-path подложки с «окнами»: box — прямоугольник подложки (вьюпорт), holes — прямоугольники слоёв ячейки (вьюпорт),
// out — поля внешнего контура за краями подложки (тень / рамка рисуются там): { l, t, r, b }.
// Окна обрезаются по подложке; ни одно не пересекает её — '' (clip-path не нужен)
export function cellPeek_clipPath(box, holes, out = { l: 14, t: 14, r: 14, b: 14 }) {
    if (!box) return ''
    const w = box.right - box.left, h = box.bottom - box.top
    const parts = []
    for (const q of holes || []) {
        const x = rect_intersect(q, box)
        if (!x || x.right - x.left < 0.5 || x.bottom - x.top < 0.5) continue
        const l = n2(x.left - box.left), t = n2(x.top - box.top), r = n2(x.right - box.left), b = n2(x.bottom - box.top)
        parts.push(`M${l} ${t}H${r}V${b}H${l}Z`)
    }
    if (!parts.length) return ''
    const L = n2(-(out.l || 0)), T = n2(-(out.t || 0)), R = n2(w + (out.r || 0)), B = n2(h + (out.b || 0))
    return `path(evenodd, '${['M' + L + ' ' + T + 'H' + R + 'V' + B + 'H' + L + 'Z', ...parts].join('')}')`
}

// ---- DOM ----

function parseAlpha(c) {
    if (!c || c === 'transparent') return 0
    const m = /rgba?\(([^)]*)\)/.exec(c)
    if (!m) return 1
    const p = m[1].split(/[\s,/]+/).filter(Boolean)
    if (p.length < 4) return 1
    const a = String(p[3])
    return a.endsWith('%') ? parseFloat(a) / 100 : parseFloat(a)
}
const box = (q) => ({ left: q.left, top: q.top, right: q.right, bottom: q.bottom })
// внутреннее поле (padding box): край, по которому режет overflow
function paddingBox(el, cs) {
    const q = el.getBoundingClientRect()
    return { left: q.left + (parseFloat(cs.borderLeftWidth) || 0), top: q.top + (parseFloat(cs.borderTopWidth) || 0),
        right: q.right - (parseFloat(cs.borderRightWidth) || 0), bottom: q.bottom - (parseFloat(cs.borderBottomWidth) || 0) }
}
const clipsX = cs => !/visible/.test(cs.overflowX) || !/visible/.test(cs.overflowY)

// элемент (или предок до ячейки) спрятан / вынесен из потока / служебный — его текст продолжение не показывает
function outOfPeek(el, root) {
    for (let p = el; p && p !== root.parentElement; p = p.parentElement) {
        if (p !== root && p.matches(CLIP_SKIP)) return true
        const cs = getComputedStyle(p)
        if (cs.display === 'none' || cs.visibility === 'hidden' || (p !== root && (cs.position === 'absolute' || cs.position === 'fixed'))) return true
        if (parseFloat(cs.opacity) === 0) return true
        if (p === root) break
    }
    return false
}

// коробка обрезки для узла: пересечение внутренних полей предков с overflow ≠ visible от родителя узла до ячейки включительно;
// у текста ближайший режущий предок с многоточием режет по своему внутреннему полю (content box) по горизонтали
function clipBoxOf(node, root, isText) {
    let c = null, first = true
    for (let p = node.parentElement; p; p = p.parentElement) {
        const cs = getComputedStyle(p)
        if (clipsX(cs) || p === root) {
            let b = paddingBox(p, cs)
            if (isText && first && /ellipsis/.test(cs.textOverflow)) b = { ...b, left: b.left + (parseFloat(cs.paddingLeft) || 0), right: b.right - (parseFloat(cs.paddingRight) || 0) }
            first = false
            c = rect_intersect(c, b)
        }
        if (p === root) break
    }
    return c
}

// замеры ячейки: строки видимого текста и «пилюли» потока против их коробок обрезки
export function cellPeek_items(cell) {
    const items = []
    if (!cell || !cell.isConnected) return items
    const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT)
    let tn
    while ((tn = walker.nextNode())) {
        if (!/\S/.test(tn.textContent || '')) continue
        const pe = tn.parentElement
        if (!pe || outOfPeek(pe, cell)) continue
        const cs = getComputedStyle(pe)
        const fill = cs.webkitTextFillColor || cs.color
        if (parseAlpha(cs.color) === 0 || parseAlpha(fill) === 0) continue   // спрятанный цветом текст (.hmg-bcell)
        const clip = clipBoxOf(tn, cell, true)
        if (!clip) continue
        let rects = []
        try { const rg = document.createRange(); rg.selectNodeContents(tn); rects = [...rg.getClientRects()] } catch (e) { rects = [] }
        for (const q of rects) items.push({ r: box(q), clip })
    }
    for (const el of cell.querySelectorAll('*')) {
        if (outOfPeek(el, cell)) continue
        const cs = getComputedStyle(el)
        const pill = parseAlpha(cs.backgroundColor) > 0 || (cs.backgroundImage && cs.backgroundImage !== 'none') ||
            ['Left', 'Right'].some(s => (parseFloat(cs['border' + s + 'Width']) || 0) > 0 && cs['border' + s + 'Style'] !== 'none')
        if (!pill) continue
        const q = el.getBoundingClientRect()
        if (!q.width || !q.height) continue
        const clip = clipBoxOf(el, cell, false)
        if (clip) items.push({ r: box(q), clip })
    }
    return items
}

export function cellPeek_truncated(cell, tol = 1) {
    try { return cellPeek_sticksOut(cellPeek_items(cell), tol) } catch (e) { return false }
}

// слои ячейки, которые продолжение не переносит (абсолютные элементы, SVG / картинки / холсты потока): их прямоугольники — «окна» подложки.
// Служебные кнопки и значки потока (стрелка списка, иконки) по-прежнему под подложкой — окно в них разрезало бы хвост текста
export function cellPeek_layers(cell) {
    const out = []
    if (!cell || !cell.isConnected) return out
    const cr = cell.getBoundingClientRect()
    for (const el of cell.querySelectorAll('*')) {
        const cs = getComputedStyle(el)
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue
        const layer = cs.position === 'absolute' || cs.position === 'fixed' || el.matches('svg, img, canvas')
        if (!layer) continue
        if (out.some(o => o.el.contains(el))) continue   // внутри уже взятого слоя
        const q = el.getBoundingClientRect()
        if (q.width < 0.5 || q.height < 0.5) continue
        const x = rect_intersect(box(q), box(cr))   // часть слоя, видимая в ячейке
        if (x.right - x.left < 0.5 || x.bottom - x.top < 0.5) continue
        out.push({ el, ...x })
    }
    return out.map(({ left, top, right, bottom }) => ({ left, top, right, bottom }))
}
